import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FitScreenOutlinedIcon from '@mui/icons-material/FitScreenOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import {
  AttioQueryKeys,
  COLUMNS_HIDDEN_BY_DEFAULT,
  COMPANY_NAME_KEY,
  FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT,
} from 'common/constants/analyst-dashboards';
import { EMPTY_VALUATIONS_TABLE } from 'common/constants/documents';
import { TertiaryNavigation, TertiaryNavigationAddButton, TertiaryNavigationIconButton } from 'components/ui';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import type { TertiaryNavigationItem } from 'components/ui/TertiaryNavigation/TertiaryNavigation.types';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { useEventSubscription } from 'context/EventBusContext/useEventSubscription';
import { useZoom } from 'context/ZoomContext';
import { ExpandableSearch, ExpandableSearchHandle } from 'dashboard409a/components/ExpandableSearch';
import NewViewDialog from 'dashboard409a/components/NewViewDialog';
import type { WorkboardView } from 'dashboard409a/components/NewViewDialog';
import {
  calculateTableHeight,
  StyledTableCellEmptyMessage,
  useNoteHandler,
} from 'dashboard409a/components/ValuationsTable/common';
import { DealStageOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import WorkboardUserSelector from 'dashboard409a/components/WorkboardUserSelector';
import { useCompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext';
import { useAttioFilters } from 'dashboard409a/hooks/useAttioFilters';
import { useKeyboardNavigation } from 'dashboard409a/hooks/useKeyboardNavigation';
import { useValuationsWithBulkData } from 'dashboard409a/hooks/useValuationsWithBulkData';
import { useWorkboardViews } from 'dashboard409a/hooks/useWorkboardViews';
import { columns } from 'dashboard409a/pages/Workboard/common/colConfig';
import { DEFAULT_NEW_VIEW_NAME } from 'dashboard409a/pages/Workboard/common/constants';
import {
  getDealStatusOptions,
  getValuationStatusOptions,
  updateValuationDealStage,
} from 'dashboard409a/services/attio-common';
import { CreateNoteRequest, SimplifiedAttioValuation } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import { useDebouncedValue } from 'hooks';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { Logger } from 'utilities/logger';
import CollapsibleTableRow from './CollapsibleTableRow/CollapsibleTableRow';
import ConfigToggler from './ConfigToggler/ConfigToggler';
import {
  EnhancedTableHeadColumn,
  ValuationsTableBulkDataProps,
  ValuationsTableValuationUpdatedPayload,
} from './ValuationsTable.types';
import ValuationsTableHead from './ValuationsTableHead';
import ValuationsTableRow from './ValuationsTableRow';

const DEFAULT_TERTIARY_ITEM: TertiaryNavigationItem = { label: 'Default', value: 'default', showMenu: false };

export const ValuationsTableBulkData = ({
  filters,
  orderBy,
  order,
  sortCriteria,
  onRequestSort,
  onRowClick,
  onVisibleValuationsChange,
  sectionId,
  pauseBatchLoading = false,
  toolbarPrefix,
  hideTable = false,
}: ValuationsTableBulkDataProps) => {
  const searchRef = useRef<ExpandableSearchHandle>(null);
  const [searchParam, setSearchParam] = useState({ searchParam: '', searchAll: false });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const debouncedSearchParam = useDebouncedValue(searchParam.searchParam, 300);

  const { views, addView, removeView, updateView, cloneView } = useWorkboardViews(sectionId);

  const defaultNewViewName = useMemo(() => {
    const lowerNames = views.map(v => v.name.toLowerCase());
    const base = DEFAULT_NEW_VIEW_NAME;
    if (!lowerNames.includes(base.toLowerCase())) return base;
    const firstCandidate = `${base} (Copy)`;
    if (!lowerNames.includes(firstCandidate.toLowerCase())) return firstCandidate;
    let i = 2;
    while (lowerNames.includes(`${base} (Copy ${i})`.toLowerCase())) {
      i++;
    }
    return `${base} (Copy ${i})`;
  }, [views]);
  const [activeViewId, setActiveViewId] = useState('default');
  const [isNewViewDialogOpen, setIsNewViewDialogOpen] = useState(false);
  const [isEditViewDialogOpen, setIsEditViewDialogOpen] = useState(false);
  const [viewToEdit, setViewToEdit] = useState<WorkboardView | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuViewId, setMenuViewId] = useState<string | null>(null);

  // Reset active view when section changes
  useEffect(() => {
    setActiveViewId('default');
  }, [sectionId]);

  // ── Note cell selection state ────────────────────────────────────────────
  const [selectedNoteIds, setSelectedNoteIds] = useState<Set<string>>(new Set());
  const [lastSelectedNoteId, setLastSelectedNoteId] = useState<string | null>(null);
  const [groupByDeals, setGroupByDeals] = useLocalStorage('groupByDeals', false);
  // Remember which deal groups are collapsed across sessions
  const [collapsedDealIds, setCollapsedDealIds] = useLocalStorage<string[]>('collapsedDealIds', []);
  const isDraggingRef = useRef(false);
  const dragAnchorIdRef = useRef<string | null>(null);

  // Stable ref so the global paste handler never becomes stale
  const pasteHandlerRef = useRef<(text: string) => Promise<void>>(async () => {});

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchRef.current?.expand();
        return;
      }

      // ── Paste to selected note cells ───────────────────────────────────
      if ((event.metaKey || event.ctrlKey) && event.key === 'v') {
        const activeEl = document.activeElement as HTMLElement | null;
        if (!activeEl?.matches('[data-notes-cell="true"]')) {
          return;
        }
        event.preventDefault();
        navigator.clipboard.readText().then(text => {
          if (text) {
            pasteHandlerRef.current(text);
          }
        });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // End drag on global mouseup
  useEffect(() => {
    const onMouseUp = () => {
      isDraggingRef.current = false;
    };
    document.addEventListener('mouseup', onMouseUp);
    return () => document.removeEventListener('mouseup', onMouseUp);
  }, []);

  // Clear selection when clicking outside any note cell
  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-notes-cell="true"]')) {
        setSelectedNoteIds(new Set());
        setLastSelectedNoteId(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);

  const {
    rows,
    groupedRows,
    totalCount: totalCountFromHook,
    isLoading,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
    updateValuationOtherConsultants,
    selectedUserEmail,
    setSelectedUserEmail,
    staffUsers,
    isLoadingStaffUsers,
    refetch: refetchValuations,
  } = useValuationsWithBulkData({
    filters,
    orderBy,
    order,
    sortCriteria,
    pauseBatchLoading,
    searchParams: {
      searchParam: debouncedSearchParam,
      searchAll: debouncedSearchParam.trim() ? searchParam.searchAll : false,
    },
    groupByDeals,
  });

  useEffect(() => {
    if (isRefreshing && !isLoading) setIsRefreshing(false);
  }, [isLoading, isRefreshing]);

  // Track color palette changes to force re-render when Done is clicked
  const [, setPaletteVersion] = useState(0);

  const handlePaletteClose = useCallback(() => {
    setPaletteVersion(prev => prev + 1);
  }, []);

  const { isNoteLoading, handleNoteSave: handleNoteSaveGeneric } = useNoteHandler({
    onSuccess: (id, noteContent, createdNote) => {
      const noteId = createdNote?.id;
      if (createdNote?.attio_valuation_id) {
        updateValuationNotes(createdNote.attio_valuation_id, noteContent, noteId);
      } else {
        updateValuationNotes(id as string, noteContent, noteId);
      }
    },
  });

  // When grouped, all valuations for lookups/selection order come from groupedRows
  const flatValuationsForLookup = useMemo(
    () => (groupByDeals ? groupedRows.flatMap(g => g.valuations) : rows),
    [groupByDeals, groupedRows, rows]
  );

  const handleNoteSave = useCallback(
    async (valuationId: string, noteContent: string, currentNote?: string) => {
      const request: CreateNoteRequest = {
        content: noteContent,
        attio_valuation_id: valuationId,
        reference_type: 'at',
      };

      const currentValuation = flatValuationsForLookup.find(r => r.record_id === valuationId);
      // A note DB record exists if note is non-null (regardless of content being empty)
      const hasExistingNote = currentValuation?.note != null;

      const existingNoteId =
        hasExistingNote && currentValuation?.note?.id ? currentValuation.note.id.toString() : undefined;

      await handleNoteSaveGeneric(valuationId, noteContent, currentNote, request, hasExistingNote, existingNoteId);
    },
    [flatValuationsForLookup, handleNoteSaveGeneric]
  );

  const getDefaultVisibility = useCallback((): Record<string, boolean> => {
    const initialVisibility: Record<string, boolean> = {};
    columns.forEach(col => {
      initialVisibility[col.id] = !COLUMNS_HIDDEN_BY_DEFAULT.includes(col.id);
    });
    return initialVisibility;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const storageKey = sectionId ? `valuations_column_visibility_${sectionId}` : '';
  const [columnVisibility, setColumnVisibility] = useLocalStorage<Record<string, boolean>>(
    storageKey || 'temp_key',
    getDefaultVisibility()
  );

  const [columnWidths, setColumnWidths] = useLocalStorage<Record<string, number>>('columnWidths', {});

  const handleColumnWidthChange = useCallback(
    (columnId: string, newWidth: number) => {
      setColumnWidths(prev => ({
        ...prev,
        [columnId]: newWidth,
      }));
    },
    [setColumnWidths]
  );

  const conditionalColumns = useMemo(
    () => columns.filter(col => columnVisibility[col.id] !== false),
    [columnVisibility]
  );

  // Check if any financial column is visible (triggers horizontal scroll)
  const hasExtraColumns = useMemo(
    () => conditionalColumns.some(col => FINANCIAL_FIELDS_HIDDEN_BY_DEFAULT.includes(col.id)),
    [conditionalColumns]
  );

  const allRows = rows;

  const [filteredRows, setFilteredRows] = useState<SimplifiedAttioValuation[]>(allRows);
  const [tableKey, setTableKey] = useState(0);

  const orderedRowsForSelection = groupByDeals ? flatValuationsForLookup : filteredRows;
  useEffect(() => {
    onVisibleValuationsChange?.(orderedRowsForSelection);
  }, [onVisibleValuationsChange, orderedRowsForSelection]);
  const { focusedRowIndex, setFocusedRowIndex, handleKeyDown } = useKeyboardNavigation(
    groupByDeals ? flatValuationsForLookup.length : filteredRows.length
  );
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const hasAutoFocused = useRef(false);

  useEffect(() => {
    setFilteredRows(allRows);
  }, [allRows]);

  // Auto-focus the first cell when rows first become available.
  // Reset when rows empty out so the next load re-triggers.
  useEffect(() => {
    if (filteredRows.length === 0) {
      hasAutoFocused.current = false;
      return;
    }
    if (hasAutoFocused.current) {
      return;
    }
    hasAutoFocused.current = true;
    const firstCell = tbodyRef.current?.querySelector<HTMLElement>('tr[tabindex] td');
    if (firstCell) {
      if (!firstCell.hasAttribute('tabindex')) {
        firstCell.setAttribute('tabindex', '-1');
      }
      firstCell.focus();
    }
  }, [filteredRows.length]);

  // ── Note selection handlers (placed after filteredRows is available) ───
  const handleNoteSelect = useCallback(
    (recordId: string, e: MouseEvent) => {
      e.stopPropagation();
      if (e.shiftKey && lastSelectedNoteId) {
        const startIdx = orderedRowsForSelection.findIndex(r => r.record_id === lastSelectedNoteId);
        const endIdx = orderedRowsForSelection.findIndex(r => r.record_id === recordId);
        const [from, to] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
        setSelectedNoteIds(new Set(orderedRowsForSelection.slice(from, to + 1).map(r => r.record_id)));
      } else {
        setSelectedNoteIds(new Set([recordId]));
        setLastSelectedNoteId(recordId);
      }
    },
    [orderedRowsForSelection, lastSelectedNoteId]
  );

  const handleNoteDoubleClick = useCallback((recordId: string) => {
    setSelectedNoteIds(new Set([recordId]));
    setLastSelectedNoteId(recordId);
  }, []);

  const handleNoteMouseDown = useCallback((recordId: string, e: MouseEvent) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    dragAnchorIdRef.current = recordId;
    if (!e.shiftKey) {
      setSelectedNoteIds(new Set([recordId]));
      setLastSelectedNoteId(recordId);
    }
  }, []);

  const handleNoteMouseEnter = useCallback(
    (recordId: string) => {
      if (!isDraggingRef.current || !dragAnchorIdRef.current) {
        return;
      }
      const startIdx = orderedRowsForSelection.findIndex(r => r.record_id === dragAnchorIdRef.current);
      const endIdx = orderedRowsForSelection.findIndex(r => r.record_id === recordId);
      const [from, to] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
      setSelectedNoteIds(new Set(orderedRowsForSelection.slice(from, to + 1).map(r => r.record_id)));
    },
    [orderedRowsForSelection]
  );

  const handlePasteToSelected = useCallback(
    async (text: string) => {
      if (!text.trim()) {
        return;
      }
      const activeEl = document.activeElement as HTMLElement | null;
      const focusedId = activeEl?.dataset?.recordId;
      let targetIds: Set<string> | null;
      if (selectedNoteIds.size > 0) {
        targetIds = selectedNoteIds;
      } else if (focusedId) {
        targetIds = new Set([focusedId]);
      } else {
        targetIds = null;
      }
      if (!targetIds) {
        return;
      }
      await Promise.all(
        Array.from(targetIds).map(async recordId => {
          const row = orderedRowsForSelection.find(r => r.record_id === recordId);
          if (row) {
            await handleNoteSave(recordId, text, row.note?.content || '');
          }
        })
      );
    },
    [selectedNoteIds, orderedRowsForSelection, handleNoteSave]
  );

  useEffect(() => {
    pasteHandlerRef.current = handlePasteToSelected;
  }, [handlePasteToSelected]);

  const rowsForColumnWidth = groupByDeals ? flatValuationsForLookup : filteredRows;
  const handleAutoFitColumns = useCallback(() => {
    const longestCompanyName = rowsForColumnWidth.reduce((longest, row) => {
      const name = row.company_name || '';
      return name.length > longest.length ? name : longest;
    }, '');

    const charWidth = 8;
    const padding = 32;
    const companyNameWidth = Math.max(150, Math.min(400, longestCompanyName.length * charWidth + padding));

    setColumnWidths({
      [COMPANY_NAME_KEY]: companyNameWidth,
    });

    setTableKey(prev => prev + 1);
  }, [setColumnWidths, rowsForColumnWidth]);

  const { applyFilters, isSearching } = useAttioFilters(allRows, setFilteredRows);

  const { data: attioStatusOptions = [], isLoading: isLoadingStatusOptions } = useQuery({
    queryKey: [AttioQueryKeys.VALUATION_STATUS_OPTIONS],
    queryFn: getValuationStatusOptions,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const { data: dealStageOptionsData = [] } = useQuery<Array<{ id?: { status_id?: string }; title?: string }>>({
    queryKey: [AttioQueryKeys.DEAL_STAGE_OPTIONS],
    queryFn: async () => {
      const response = await getDealStatusOptions();
      return (response.data || []) as Array<{ id?: { status_id?: string }; title?: string }>;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const statusOptions = useMemo(
    () => attioStatusOptions.map(option => ({
      id: {
        workspace_id: option.id.workspace_id,
        object_id: option.id.object_id,
        attribute_id: option.id.attribute_id,
        option_id: option.id.option_id,
      },
      title: option.title,
      is_archived: option.is_archived,
    })),
    [attioStatusOptions]
  );
  const filterStatusOptions = useMemo(
    () => statusOptions.filter(option => flatValuationsForLookup.some(row => row.status === option.title)),
    [statusOptions, flatValuationsForLookup]
  );
  const statusOptionsLoading = isLoadingStatusOptions;

  const user = useSelector((state: RootStateType) => state.user);
  const { zoomLevel } = useZoom();
  const tableHeight = useMemo(() => calculateTableHeight(zoomLevel, 200), [zoomLevel]);

  const { setSecondaryNavActionsNode, setTertiaryNavNode } = useCompanyDashboardContext();

  const currentUserDisplayName = useMemo(
    () => [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'User',
    [user?.email, user?.first_name, user?.last_name]
  );

  const userSelectorNode = useMemo(
    () =>
      user?.is_staff ? (
        <WorkboardUserSelector
          currentUserDisplayName={currentUserDisplayName}
          isLoading={isLoadingStaffUsers}
          selectedUserEmail={selectedUserEmail}
          setSelectedUserEmail={setSelectedUserEmail}
          staffUsers={staffUsers}
        />
      ) : null,
    [currentUserDisplayName, isLoadingStaffUsers, selectedUserEmail, setSelectedUserEmail, staffUsers, user?.is_staff]
  );

  useEffect(() => {
    setSecondaryNavActionsNode?.(userSelectorNode);
    return () => {
      setSecondaryNavActionsNode?.(null);
    };
  }, [setSecondaryNavActionsNode, userSelectorNode]);

  // Subscribe to valuation updates from OperationsModal
  useEventSubscription<ValuationsTableValuationUpdatedPayload>(
    EventType.WORKBOARD_VALUATION_UPDATED,
    useCallback(
      (payload: ValuationsTableValuationUpdatedPayload) => {
        const { valuationId, updates } = payload;

        const applyUpdatesToRow = (row: SimplifiedAttioValuation): SimplifiedAttioValuation => {
          if (row.record_id !== valuationId) {
            return row;
          }
          const updatedRow = { ...row };
          if (updates.status) {
            updatedRow.status = updates.status;
          }
          if (updates.dealStage) {
            updatedRow.deal_stage = updates.dealStage;
          }
          if (updates.deal) {
            if (updates.deal.value) {
              updatedRow.deal_value = updates.deal.value;
            }
            if (updates.deal.due) {
              updatedRow.deal_due = updates.deal.due;
            }
            if (updates.deal.stage) {
              updatedRow.deal_stage = updates.deal.stage;
            }
          }
          return updatedRow;
        };

        setFilteredRows(prevRows => prevRows.map(applyUpdatesToRow));
        updateValuationStatus(valuationId, updates.status || '');
      },
      [updateValuationStatus]
    )
  );

  const handleDealStageChange = useCallback(
    async (row: SimplifiedAttioValuation, dealStageId: string) => {
      if (!row.deal_id) {
        enqueueSnackbar('Cannot update deal stage: deal_id is missing', { variant: 'error' });
        return;
      }

      const dealId = row.deal_id;
      try {
        await updateValuationDealStage(dealId, dealStageId);

        // Find the deal stage label from the options (same structure as ValuationDealInfo expects)
        const selectedOption = dealStageOptionsData.find(option => option.id?.status_id === dealStageId);
        const newDealStage = selectedOption?.title || row.deal_stage;

        // Update local state
        setFilteredRows(prevRows =>
          prevRows.map(r => (r.record_id === row.record_id ? { ...r, deal_stage: newDealStage } : r))
        );
      } catch (error) {
        Logger.error('Error updating deal stage', error);
      }
    },
    [dealStageOptionsData]
  );

  const handleRowClick = (row: SimplifiedAttioValuation, event: React.MouseEvent<HTMLTableRowElement>) => {
    onRowClick(row, event);
  };

  const handleColumnsToggle = useCallback((columnId: string) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  }, [setColumnVisibility]);

  const handleToggleCollapsedDeal = (dealKey: string) => {
    setCollapsedDealIds(prev => {
      const current = Array.isArray(prev) ? prev : [];
      return current.includes(dealKey) ? current.filter(id => id !== dealKey) : [...current, dealKey];
    });
  };

  const handleAddView = useCallback(() => setIsNewViewDialogOpen(true), []);

  const handleCreateView = useCallback(
    (view: Parameters<typeof addView>[0]) => {
      addView(view);
      setActiveViewId(view.id);
      setColumnVisibility({ ...view.columnVisibility });
      setGroupByDeals(view.filters.groupByDeals);
      applyFilters({
        searchParams: { searchParam: '', searchAll: false },
        searchStatus: view.filters.searchStatus,
        fromDate: view.filters.fromDate ? new Date(view.filters.fromDate) : null,
        toDate: view.filters.toDate ? new Date(view.filters.toDate) : null,
      });
    },
    [addView, applyFilters, setColumnVisibility, setGroupByDeals]
  );

  const handleTabChange = useCallback(
    (viewId: string) => {
      setActiveViewId(viewId);
      if (viewId === 'default') {
        setColumnVisibility(getDefaultVisibility());
        setGroupByDeals(false);
        applyFilters({
          searchParams: { searchParam: '', searchAll: false },
          searchStatus: 'default',
          fromDate: null,
          toDate: null,
        });
      } else {
        const view = views.find(v => v.id === viewId);
        if (view) {
          setColumnVisibility({ ...view.columnVisibility });
          setGroupByDeals(view.filters.groupByDeals);
          applyFilters({
            searchParams: { searchParam: '', searchAll: false },
            searchStatus: view.filters.searchStatus,
            fromDate: view.filters.fromDate ? new Date(view.filters.fromDate) : null,
            toDate: view.filters.toDate ? new Date(view.filters.toDate) : null,
          });
        }
      }
    },
    [views, getDefaultVisibility, applyFilters, setColumnVisibility, setGroupByDeals]
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setMenuViewId(null);
  }, []);

  const handleItemMenuClick = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>, item: TertiaryNavigationItem) => {
      event.stopPropagation();
      setMenuViewId(item.value);
      setMenuAnchor(event.currentTarget);
    },
    []
  );

  const handleEditView = useCallback(() => {
    handleMenuClose();
    const view = views.find(v => v.id === menuViewId);
    if (view) {
      setViewToEdit(view);
      setIsEditViewDialogOpen(true);
    }
  }, [handleMenuClose, menuViewId, views]);

  const handleSaveEditedView = useCallback(
    (updated: WorkboardView) => {
      updateView(updated);
      if (activeViewId === updated.id) {
        setColumnVisibility({ ...updated.columnVisibility });
        setGroupByDeals(updated.filters.groupByDeals);
        applyFilters({
          searchParams: { searchParam: '', searchAll: false },
          searchStatus: updated.filters.searchStatus,
          fromDate: updated.filters.fromDate ? new Date(updated.filters.fromDate) : null,
          toDate: updated.filters.toDate ? new Date(updated.filters.toDate) : null,
        });
      }
    },
    [updateView, activeViewId, applyFilters, setColumnVisibility, setGroupByDeals]
  );

  const handleCloneView = useCallback(() => {
    handleMenuClose();
    if (menuViewId) cloneView(menuViewId);
  }, [handleMenuClose, menuViewId, cloneView]);

  const handleDeleteView = useCallback(() => {
    handleMenuClose();
    if (menuViewId) {
      removeView(menuViewId);
      if (activeViewId === menuViewId) handleTabChange('default');
    }
  }, [handleMenuClose, menuViewId, removeView, activeViewId, handleTabChange]);

  const tertiaryItems = useMemo<TertiaryNavigationItem[]>(
    () => [
      DEFAULT_TERTIARY_ITEM,
      ...views.map(v => ({ label: v.name, value: v.id, showMenu: true, isClosable: true })),
    ],
    [views]
  );

  const tertiaryLeftActions = useMemo(
    () => <TertiaryNavigationAddButton onClick={handleAddView} />,
    [handleAddView]
  );

  const tertiaryNavActionsNode = useMemo(
    () => (
      <Box display="flex" alignItems="center" gap={1}>
        {toolbarPrefix}
        <ConfigToggler
          groupByDeals={groupByDeals}
          columns={columns as EnhancedTableHeadColumn[]}
          columnVisibility={columnVisibility}
          onColumnsToggle={handleColumnsToggle}
          onPaletteClose={handlePaletteClose}
          onGroupByDealsToggle={() => setGroupByDeals(!groupByDeals)}
        />
        {!hideTable && (
          <TertiaryNavigationIconButton
            ariaLabel="Auto-fit columns"
            icon={<FitScreenOutlinedIcon />}
            tooltipTitle="Auto-fit Columns"
            variant="toolbar"
            onClick={handleAutoFitColumns}
          />
        )}
        <ExpandableSearch
          isValuationsPage
          ref={searchRef}
          value={searchParam.searchParam}
          onChange={e => setSearchParam(prev => ({ ...prev, searchParam: e.target.value }))}
          placeholder="Search by company..."
          searchAll={searchParam.searchAll}
          onSearchAllChange={v => setSearchParam(prev => ({ ...prev, searchAll: v }))}
        />
        <TertiaryNavigationIconButton
          ariaLabel="Refresh data"
          icon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          tooltipTitle="Refresh data"
          variant="toolbar"
          disabled={isRefreshing}
          onClick={() => {
            setIsRefreshing(true);
            refetchValuations();
          }}
        />
        {isLoading || isRefreshing ? (
          <Skeleton variant="rounded" width={140} height={28} sx={{ borderRadius: '8px' }} />
        ) : (
          <Box sx={{
            backgroundColor: 'action.hover',
            borderRadius: '8px',
            px: 1.5,
            py: 0.5,
            display: 'inline-flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
          }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Total Valuations: {totalCountFromHook.toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>
    ),
    [
      toolbarPrefix, groupByDeals, columnVisibility, handleColumnsToggle, handlePaletteClose,
      hideTable, handleAutoFitColumns, searchParam, isRefreshing, refetchValuations,
      isLoading, totalCountFromHook, setGroupByDeals,
    ]
  );

  const tertiaryNavNode = useMemo(
    () => (
      <TertiaryNavigation
        items={tertiaryItems}
        leftActions={tertiaryLeftActions}
        actions={tertiaryNavActionsNode}
        value={activeViewId}
        onChange={handleTabChange}
        onItemClose={(_, item) => {
          removeView(item.value);
          if (activeViewId === item.value) handleTabChange('default');
        }}
        onItemMenuClick={handleItemMenuClick}
        zoomLevel={zoomLevel}
      />
    ),
    [tertiaryNavActionsNode, zoomLevel, tertiaryItems, tertiaryLeftActions, activeViewId, handleTabChange, removeView, handleItemMenuClick]
  );

  useEffect(() => {
    setTertiaryNavNode?.(tertiaryNavNode);
    return () => {
      setTertiaryNavNode?.(null);
    };
  }, [setTertiaryNavNode, tertiaryNavNode]);

  const renderTableBody = () => {
    const commonRowProps = {
      onRowClick: handleRowClick,
      onStatusUpdate: (updatedRow: SimplifiedAttioValuation) =>
        updateValuationStatus(updatedRow.record_id, updatedRow.status || ''),
      onNoteUpdate: (updatedRow: SimplifiedAttioValuation) =>
        updateValuationNotes(updatedRow.record_id, updatedRow.note?.content || ''),
      onEngagementManagerUpdate: updateValuationEngagementManager,
      onOtherConsultantsUpdate: updateValuationOtherConsultants,
      statusOptions,
      statusOptionsLoading,
      isNoteLoading,
      columns: conditionalColumns as EnhancedTableHeadColumn[],
      columnWidths,
      dealStageOptions: dealStageOptionsData as DealStageOption[],
      onFocus: setFocusedRowIndex,
      onNoteSelect: handleNoteSelect,
      onNoteDoubleClick: handleNoteDoubleClick,
      onNoteMouseDown: handleNoteMouseDown,
      onNoteMouseEnter: handleNoteMouseEnter,
    };

    if (isLoading || isRefreshing) {
      return (
        <TableRow>
          <StyledTableCellEmptyMessage colSpan={conditionalColumns.length + 3}>
            <CircularProgress size={24} />
          </StyledTableCellEmptyMessage>
        </TableRow>
      );
    }

    if (groupByDeals) {
      if (groupedRows.length === 0) {
        return (
          <TableRow>
            <StyledTableCellEmptyMessage colSpan={conditionalColumns.length + 3}>
              {EMPTY_VALUATIONS_TABLE}
            </StyledTableCellEmptyMessage>
          </TableRow>
        );
      }
      const colSpan = conditionalColumns.length + 3;
      let rowIndexOffset = 0;
      return groupedRows.map(group => {
        const rowIndexStart = rowIndexOffset;
        rowIndexOffset += group.valuations.length;
        const dealKey = group.deal_id ?? '__no_deal__';
        if (group.valuations.length > 1) {
          const renderGroupChildren = (valuations: typeof group.valuations) =>
            valuations.map((row, i) => (
              <ValuationsTableRow
                {...commonRowProps}
                key={row.record_id}
                valuationRow={row}
                isGroupChild
                isLastGroupChild={i === valuations.length - 1}
                onNoteSave={noteContent => {
                  const currentNoteContent = row.note?.content || '';
                  return handleNoteSave(row.record_id, noteContent, currentNoteContent);
                }}
                onDealStageChange={async (dealStageId: string) => {
                  await handleDealStageChange(row, dealStageId);
                }}
                tabIndex={focusedRowIndex === rowIndexStart + i ? 0 : -1}
                rowIndex={rowIndexStart + i}
                isNoteSelected={selectedNoteIds.has(row.record_id)}
              />
            ));
          return (
            <CollapsibleTableRow
              key={dealKey}
              group={group}
              colSpan={colSpan}
              rowIndexStart={rowIndexStart}
              isCollapsed={collapsedDealIds.includes(dealKey)}
              onToggleCollapse={() => handleToggleCollapsedDeal(dealKey)}
              renderChildRows={renderGroupChildren}
            />
          );
        }
        return (
          <ValuationsTableRow
            {...commonRowProps}
            key={dealKey}
            valuationRow={group.valuations[0]}
            onNoteSave={noteContent => {
              const currentNoteContent = group.valuations[0].note?.content || '';
              return handleNoteSave(group.valuations[0].record_id, noteContent, currentNoteContent);
            }}
            onDealStageChange={async (dealStageId: string) => {
              await handleDealStageChange(group.valuations[0], dealStageId);
            }}
            tabIndex={focusedRowIndex === rowIndexStart ? 0 : -1}
            rowIndex={rowIndexStart}
            isNoteSelected={selectedNoteIds.has(group.valuations[0].record_id)}
          />
        );
      });
    }

    if (filteredRows.length === 0) {
      return (
        <TableRow>
          <StyledTableCellEmptyMessage colSpan={conditionalColumns.length + 3}>
            {EMPTY_VALUATIONS_TABLE}
          </StyledTableCellEmptyMessage>
        </TableRow>
      );
    }

    return filteredRows.map((row, i) => (
      <ValuationsTableRow
        {...commonRowProps}
        key={row.record_id}
        valuationRow={row}
        onNoteSave={noteContent => {
          const currentNoteContent = row.note?.content || '';
          return handleNoteSave(row.record_id, noteContent, currentNoteContent);
        }}
        onDealStageChange={async (dealStageId: string) => {
          await handleDealStageChange(row, dealStageId);
        }}
        tabIndex={focusedRowIndex === i ? 0 : -1}
        rowIndex={i}
        isNoteSelected={selectedNoteIds.has(row.record_id)}
      />
    ));
  };

  return (
    <>
      <NewViewDialog
        open={isNewViewDialogOpen}
        onClose={() => setIsNewViewDialogOpen(false)}
        onCreateView={handleCreateView}
        currentColumnVisibility={columnVisibility}
        currentGroupByDeals={groupByDeals as boolean}
        statusOptions={filterStatusOptions}
        statusOptionsLoading={statusOptionsLoading}
        staffUsers={staffUsers}
        defaultName={defaultNewViewName}
      />
      <NewViewDialog
        open={isEditViewDialogOpen}
        onClose={() => {
          setIsEditViewDialogOpen(false);
          setViewToEdit(null);
        }}
        onCreateView={handleSaveEditedView}
        currentColumnVisibility={columnVisibility}
        currentGroupByDeals={groupByDeals as boolean}
        statusOptions={filterStatusOptions}
        statusOptionsLoading={statusOptionsLoading}
        staffUsers={staffUsers}
        initialView={viewToEdit ?? undefined}
      />
      <PrimaryNavigationMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuViewId)}
        onClose={handleMenuClose}
        menuMinWidth={0}
        menuWidth="content"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <PrimaryNavigationMenuItem icon={<EditIcon fontSize="inherit" />} onClick={handleEditView}>
          Edit View
        </PrimaryNavigationMenuItem>
        <PrimaryNavigationMenuItem icon={<ContentCopyIcon fontSize="inherit" />} onClick={handleCloneView}>
          Clone
        </PrimaryNavigationMenuItem>
        <Divider />
        <PrimaryNavigationMenuItem icon={<DeleteIcon fontSize="inherit" />} onClick={handleDeleteView} tone="danger">
          Delete
        </PrimaryNavigationMenuItem>
      </PrimaryNavigationMenu>
      {!hideTable && (
        <TableContainer
          sx={{
            overflowX: 'auto',
            overflowY: 'auto',
            maxWidth: '100%',
            height: tableHeight,
            position: 'relative',
          }}>
          {isSearching && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                zIndex: 1,
              }}>
              <CircularProgress size={32} />
            </Box>
          )}
          <Table
            key={tableKey}
            sx={{
              minWidth: 650,
              tableLayout: 'auto',
              width: hasExtraColumns ? 'max-content' : '100%',
            }}>
            <ValuationsTableHead
              orderBy={orderBy}
              order={order}
              sortCriteria={sortCriteria}
              onRequestSort={onRequestSort}
              columns={conditionalColumns as EnhancedTableHeadColumn[]}
              columnWidths={columnWidths}
              onColumnWidthChange={handleColumnWidthChange}
            />
            <TableBody ref={tbodyRef} onKeyDown={handleKeyDown}>
              {renderTableBody()}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
